/**
 * Short-lived localStorage cache for directory query results.
 *
 * Entries are scoped to the authenticated user and dropped whenever that scope
 * changes. `GET /api/clients` returns only the clients the caller may raise jobs
 * against, so an unscoped cache would hand the next account to sign in on a
 * shared machine the previous user's client names and emails for the TTL.
 */

const TTL = 30_000;
const PREFIX = "afs:ac:";
const ANONYMOUS = "anon";

let scope = ANONYMOUS;

function storageKey(key: string): string {
  return `${PREFIX}${scope}:${key}`;
}

/** Drops every cached entry, whatever its scope. */
export function clearCache(): void {
  try {
    const doomed: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(PREFIX)) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* storage unavailable; nothing cached to drop */
  }
}

/**
 * Points the cache at a user. Passing `null` (sign-out) returns it to the
 * anonymous scope. Any change of scope clears what the previous one cached, so
 * entries never outlive the session that fetched them.
 */
export function setCacheScope(userId: string | null): void {
  const next = userId ?? ANONYMOUS;
  if (next === scope) return;
  clearCache();
  scope = next;
}

export function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const { value, expires } = JSON.parse(raw) as { value: T; expires: number };
    if (Date.now() > expires) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function writeCache<T>(key: string, value: T, ttl = TTL): void {
  try {
    localStorage.setItem(storageKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch {
    /* ignore quota / serialization errors */
  }
}
